import { useCallback, useEffect, useMemo, useState } from "react";

import { useShallow } from "zustand/react/shallow";

import {
  getErrorMessage,
  useMutationRequest,
  useRequest,
} from "~/v2/api/hooks";
import {
  getSessionStart,
  getWatchlistAssetMeta,
  getWatchlistSparklines,
  watchlistMarketDataQueryKeys,
} from "~/v3-poc/api/watchlistMarketData";
import {
  addWatchlistSymbol,
  createWatchlist,
  isNotFoundError,
  membershipSymbols,
  renameWatchlist,
  watchlistsQueryKeys,
} from "~/v3-poc/api/watchlists";
import {
  useGetWatchlist,
  useGetWatchlists,
} from "~/v3-poc/hooks/useWatchlists";
import { useInvalidate } from "~/v3-poc/kernel/react/Invalidation.context";
import { useGroupSymbolMutation } from "../../shared/groups/useGroupSymbolMutation";
import {
  useLiveQuotes,
  useSetSubscribedSymbols,
} from "../../shared/snapshotStream/SnapshotStream.context";
import {
  useWorkspace,
  useWorkspaceStore,
} from "../../shared/workspace/Workspace.context";
import type { GroupId } from "../../shared/workspace/workspace.types";
import type { WatchlistAddSearchProps } from "./WatchlistAddSearch.connector";
import type { WatchlistNameDialogProps } from "./WatchlistNameDialog.connector";
import type { WatchlistNameDialogMode } from "./WatchlistNameDialog.controller";
import type { WatchlistSetupPresentationProps } from "./WatchlistSetup.pres";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type WatchlistPriceTick = "up" | "down" | "flat";

export interface WatchlistRow {
  symbol: string;
  isSelected: boolean;
  /** Asset name, absent until the metadata lookup resolves (or if it fails). */
  name?: string;
  isCrypto: boolean;
  price?: number;
  /** Percentage change against the previous close. */
  changePct?: number;
  /** Today's intraday closes, oldest → newest. Empty while bars load. */
  spark: number[];
  tick: WatchlistPriceTick;
}

/** Per-symbol display data resolved by the connector. */
export interface WatchlistSymbolData {
  name?: string;
  isCrypto?: boolean;
  price?: number;
  previousClose?: number;
  spark?: number[];
  tick?: WatchlistPriceTick;
}

export interface WatchlistWidgetControllerInput {
  /** Gobroker watchlist membership, in list order. */
  membershipSymbols: string[];
  /** Quotes, names, sparklines and tick direction, keyed by symbol. */
  symbolData?: Record<string, WatchlistSymbolData>;
  /** `widget.groupId` — null when the widget is unassigned. */
  groupId: GroupId | null;
  /** `group.linkState.symbol` when grouped and a symbol has been published. */
  groupSymbol?: string;
  /** `widget.localSymbol` — the ungrouped selection. */
  localSymbol?: string;
  widgetId: string;
  // Workspace store actions
  setGroupSymbol: (groupId: GroupId, symbol?: string) => void;
  setWidgetLocalSymbol: (widgetId: string, symbol?: string) => void;
}

export interface WatchlistWidgetControllerOutput {
  rows: WatchlistRow[];
  /** Toggles a visible watchlist row against the effective symbol. */
  onToggle: (symbol: string) => void;
}

export interface UseWatchlistWidgetControllerInput {
  id: string;
  accountId?: string;
}

export type WatchlistWidgetView =
  | { status: "setup"; setup: WatchlistSetupPresentationProps }
  | { status: "loading" }
  | { status: "error"; onRetry: () => void }
  | {
      status: "bound";
      rows: WatchlistRow[];
      addSearch: WatchlistAddSearchProps;
      groupSymbolError?: string;
      onToggle: (symbol: string) => void;
    };

export interface UseWatchlistWidgetControllerOutput {
  view: WatchlistWidgetView;
  showRenameAction: boolean;
  onRename: () => void;
  nameDialog: WatchlistNameDialogProps | null;
}

/** Prices arrive on the session snapshot stream; sparklines stay on REST. */
const SPARKLINE_POLLING_INTERVAL = 60_000;

const QUIET_QUERY = { meta: { showErrorToast: false } } as const;

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

/**
 * Pure factory — takes plain values and action callbacks, returns derived
 * rows and event handlers. No React, no hooks.
 *
 * Rows are gobroker membership only, in membership order. The effective
 * "selected" symbol is the group's published symbol when grouped, or the
 * widget's `localSymbol` when ungrouped — a selection off the membership
 * list simply leaves every row unselected rather than appending a row.
 */
export const createWatchlistWidgetController = ({
  membershipSymbols,
  symbolData,
  groupId,
  groupSymbol,
  localSymbol,
  widgetId,
  setGroupSymbol,
  setWidgetLocalSymbol,
}: WatchlistWidgetControllerInput): WatchlistWidgetControllerOutput => {
  const isGrouped = groupId !== null;
  const selectedSymbol = isGrouped ? groupSymbol : localSymbol;

  const toRow = (symbol: string): WatchlistRow => {
    const data = symbolData?.[symbol];
    const { price, previousClose } = data ?? {};
    return {
      symbol,
      isSelected: symbol === selectedSymbol,
      name: data?.name,
      isCrypto: data?.isCrypto ?? symbol.includes("/"),
      price,
      changePct:
        price !== undefined && previousClose !== undefined && previousClose > 0
          ? ((price - previousClose) / previousClose) * 100
          : undefined,
      spark: data?.spark ?? [],
      tick: data?.tick ?? "flat",
    };
  };

  const rows: WatchlistRow[] = membershipSymbols.map((symbol) => toRow(symbol));

  const applySymbol = (symbol?: string) => {
    if (isGrouped && groupId !== null) {
      setGroupSymbol(groupId, symbol);
    } else {
      setWidgetLocalSymbol(widgetId, symbol);
    }
  };

  return {
    rows,

    onToggle: (symbol: string) => {
      applySymbol(symbol === selectedSymbol ? undefined : symbol);
    },
  };
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Binds a workspace widget to a gobroker watchlist. `widget.watchlistId` is the
 * only persisted state — name and membership always come from the detail query,
 * so two widgets on the same list share one cache entry and stay in step.
 *
 * Publishing rules:
 * - Grouped: a row click calls `setGroupSymbol(groupId, symbol)`, which
 *   atomically clears incompatible option legs in the group.
 * - Ungrouped: a row click calls `setWidgetLocalSymbol(id, symbol)` — local only.
 * - Clicking the selected row again clears the effective symbol.
 */
export const useWatchlistWidgetController = ({
  id,
  accountId,
}: UseWatchlistWidgetControllerInput): UseWatchlistWidgetControllerOutput => {
  // ── Workspace selectors ─────────────────────────────────────────────────
  const { groupId, groupSymbol, localSymbol, watchlistId } = useWorkspace(
    useShallow((s) => {
      const widget = s.widgets[id];
      const group =
        widget?.groupId != null ? s.groups[widget.groupId] : undefined;
      return {
        groupId: (widget?.groupId ?? null) as GroupId | null,
        groupSymbol: group?.linkState.symbol,
        localSymbol: widget?.localSymbol,
        watchlistId: widget?.watchlistId,
      };
    }),
  );

  // ── Actions from vanilla store API (stable references, no subscription) ──
  const store = useWorkspaceStore();
  const { setGroupSymbol, groupSymbolError } = useGroupSymbolMutation(groupId);
  const setWidgetLocalSymbol = useCallback(
    (widgetId: string, symbol?: string) =>
      store.getState().setWidgetLocalSymbol(widgetId, symbol),
    [store],
  );
  const setWatchlistId = useCallback(
    (next?: string) => store.getState().setWidgetWatchlistId(id, next),
    [store, id],
  );

  const invalidate = useInvalidate();

  const [nameDialogMode, setNameDialogMode] =
    useState<WatchlistNameDialogMode | null>(null);
  const [nameDialogError, setNameDialogError] = useState<string | null>(null);
  const [addError, setAddError] = useState<string>();

  // ── Gobroker watchlists ──────────────────────────────────────────────────
  const listsQuery = useGetWatchlists({
    accountID: accountId,
    options: { ...QUIET_QUERY, enabled: !watchlistId },
  });
  const detailQuery = useGetWatchlist({
    accountID: accountId,
    watchlistId,
    options: QUIET_QUERY,
  });

  const detail = detailQuery.data;
  const membership = useMemo(
    () => (detail ? membershipSymbols(detail) : []),
    [detail],
  );
  const membershipSet = useMemo(() => new Set(membership), [membership]);

  const detailError = detailQuery.error;
  const isMissing = Boolean(detailError) && isNotFoundError(detailError);

  // A deleted list must not leave the widget stuck on an error; drop the bind
  // and fall back to setup.
  useEffect(() => {
    if (isMissing) {
      setWatchlistId(undefined);
    }
  }, [isMissing, setWatchlistId]);

  const invalidateDetail = useCallback(() => {
    if (!accountId || !watchlistId) {
      return;
    }
    invalidate({
      queryKey: watchlistsQueryKeys.detail(accountId, watchlistId),
    });
  }, [invalidate, accountId, watchlistId]);

  const invalidateList = useCallback(() => {
    if (!accountId) {
      return;
    }
    invalidate({ queryKey: watchlistsQueryKeys.list(accountId) });
  }, [invalidate, accountId]);

  // `onError` is supplied on every mutation so `useMutationRequest` skips its
  // default toast — these failures are rendered in the dialog / add-search.
  const createMutation = useMutationRequest(createWatchlist, {
    onSuccess: (created) => {
      setWatchlistId(created.id);
      setNameDialogMode(null);
      invalidateList();
    },
    onError: (error) => setNameDialogError(getErrorMessage(error)),
  });

  const renameMutation = useMutationRequest(renameWatchlist, {
    onSuccess: () => {
      setNameDialogMode(null);
      invalidateDetail();
      invalidateList();
    },
    onError: (error) => setNameDialogError(getErrorMessage(error)),
  });

  const addMutation = useMutationRequest(addWatchlistSymbol, {
    onSuccess: () => {
      setAddError(undefined);
      invalidateDetail();
    },
    onError: (error) => setAddError(getErrorMessage(error)),
  });

  const handleAdd = useCallback(
    async (symbol: string) => {
      if (!accountId || !watchlistId) {
        return;
      }
      setAddError(undefined);
      // Rejects on failure so the add-search keeps its query open for `error`.
      await addMutation.mutateAsync({
        accountId,
        watchlistId,
        symbol,
      });
    },
    [accountId, watchlistId, addMutation],
  );

  const handleNameSubmit = useCallback(
    (name: string) => {
      if (!accountId) {
        return;
      }
      setNameDialogError(null);
      if (nameDialogMode === "rename") {
        if (!watchlistId) {
          return;
        }
        renameMutation.mutate({
          accountId,
          watchlistId,
          name,
          symbols: membership,
        });
        return;
      }
      createMutation.mutate({ accountId, name });
    },
    [
      accountId,
      nameDialogMode,
      watchlistId,
      membership,
      renameMutation,
      createMutation,
    ],
  );

  const openNameDialog = useCallback((mode: WatchlistNameDialogMode) => {
    setNameDialogError(null);
    setNameDialogMode(mode);
  }, []);

  const onRename = useCallback(() => {
    openNameDialog("rename");
  }, [openNameDialog]);

  // ── Market data ──────────────────────────────────────────────────────────
  const dataSymbols = membership;
  const setSubscribedSymbols = useSetSubscribedSymbols();
  const livePrices = useLiveQuotes(dataSymbols);

  useEffect(() => {
    setSubscribedSymbols(id, dataSymbols);
    return () => setSubscribedSymbols(id, []);
  }, [id, dataSymbols, setSubscribedSymbols]);

  // Recomputed per render rather than memoised on a date: the session anchor
  // only moves at midnight, and pinning it would stale the key for a day.
  const sessionStart = getSessionStart();

  const { data: sparklines } = useRequest(
    watchlistMarketDataQueryKeys.sparklines(dataSymbols, sessionStart),
    () => getWatchlistSparklines({ symbols: dataSymbols, sessionStart }),
    {
      enabled: dataSymbols.length > 0,
      refetchInterval: SPARKLINE_POLLING_INTERVAL,
      staleTime: SPARKLINE_POLLING_INTERVAL,
      ...QUIET_QUERY,
    },
  );

  const { data: assetMeta } = useRequest(
    watchlistMarketDataQueryKeys.assetMeta(dataSymbols),
    () => getWatchlistAssetMeta(dataSymbols),
    {
      enabled: dataSymbols.length > 0,
      staleTime: Number.POSITIVE_INFINITY,
      ...QUIET_QUERY,
    },
  );

  const symbolData = useMemo(() => {
    const data: Record<string, WatchlistSymbolData> = {};
    for (const symbol of dataSymbols) {
      const live = livePrices[symbol];
      data[symbol] = {
        name: assetMeta?.[symbol]?.name,
        isCrypto: assetMeta?.[symbol]?.isCrypto,
        price: live?.price,
        previousClose: live?.previousClose,
        spark: sparklines?.[symbol],
        tick: live?.tick,
      };
    }
    return data;
  }, [dataSymbols, livePrices, assetMeta, sparklines]);

  const controller = createWatchlistWidgetController({
    membershipSymbols: membership,
    symbolData,
    groupId,
    groupSymbol,
    localSymbol,
    widgetId: id,
    setGroupSymbol,
    setWidgetLocalSymbol,
  });

  // ── View ────────────────────────────────────────────────────────────────
  const buildView = (): WatchlistWidgetView => {
    if (!accountId) {
      return {
        status: "setup",
        setup: {
          lists: [],
          isLoading: true,
          isDisabled: true,
          onSelectExisting: setWatchlistId,
          onCreate: () => openNameDialog("create"),
        },
      };
    }

    if (!watchlistId) {
      return {
        status: "setup",
        setup: {
          lists: (listsQuery.data ?? []).map((list) => ({
            id: list.id,
            name: list.name,
          })),
          isLoading: listsQuery.isLoading,
          isDisabled: false,
          onSelectExisting: setWatchlistId,
          onCreate: () => openNameDialog("create"),
        },
      };
    }

    // A 404 is transient here — the unbind effect turns it into setup on the
    // next commit, so showing the retry surface would only flicker.
    if (isMissing) {
      return { status: "loading" };
    }
    // A refetch failure with a cached `detail` (e.g. a rename retry) should
    // keep showing bound rows rather than blank to the error page.
    if (detailError && !detail) {
      return { status: "error", onRetry: () => void detailQuery.refetch() };
    }
    if (!detail) {
      return { status: "loading" };
    }

    return {
      status: "bound",
      rows: controller.rows,
      addSearch: {
        membership: membershipSet,
        onAdd: handleAdd,
        error: addError,
        isPending: addMutation.isPending,
      },
      groupSymbolError,
      onToggle: controller.onToggle,
    };
  };

  const view = buildView();

  const nameDialog: WatchlistNameDialogProps | null = nameDialogMode
    ? {
        mode: nameDialogMode,
        initialName: nameDialogMode === "rename" ? (detail?.name ?? "") : "",
        error: nameDialogError,
        isSubmitting: createMutation.isPending || renameMutation.isPending,
        onClose: () => setNameDialogMode(null),
        onSubmit: handleNameSubmit,
      }
    : null;

  return {
    view,
    showRenameAction: view.status === "bound",
    onRename,
    nameDialog,
  };
};
